const initializeFinishImageModal = () => {
  const finishImageModal = document.querySelector('[data-finish-image-modal]');

  if (!finishImageModal) return;

  const finishModalImage = finishImageModal.querySelector('[data-finish-modal-image]');
  const finishModalTitle = finishImageModal.querySelector('#finish-image-modal-title');
  const finishModalViewport = finishImageModal.querySelector('[data-finish-modal-pan]');
  const finishModalCloseButtons = finishImageModal.querySelectorAll('[data-finish-modal-close]');
  const finishModalCloseButton = finishImageModal.querySelector('.finish-image-modal__close');
  const finishModalDialog = finishImageModal.querySelector('.finish-image-modal__dialog');
  const expandableFinishes = document.querySelectorAll('[data-finish-expandable="true"]');

  let panStartX = 0;
  let panStartY = 0;
  let activePointerId = null;
  let lastFocusedFinish = null;
  let hasDraggedFinishImage = false;
  let panStartedOnImage = false;
  let previousBodyPaddingRight = '';

  const isPointerOutsideFinishImage = event => {
    const imageRect = finishModalImage.getBoundingClientRect();

    return (
      event.clientX < imageRect.left ||
      event.clientX > imageRect.right ||
      event.clientY < imageRect.top ||
      event.clientY > imageRect.bottom
    );
  };

  const updateCloseTargetCursor = event => {
    if (activePointerId !== null) return;

    finishModalViewport.classList.toggle(
      'finish-image-modal__viewport--close-target',
      isPointerOutsideFinishImage(event),
    );
  };

  const lockFinishImageModalScroll = () => {
    const viewportWidth = document.documentElement.clientWidth;
    const scrollbarWidth = viewportWidth > 0 ? window.innerWidth - viewportWidth : 0;

    previousBodyPaddingRight = document.body.style.paddingRight;

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    document.body.classList.add('finish-image-modal-open');
  };

  const unlockFinishImageModalScroll = () => {
    document.body.classList.remove('finish-image-modal-open');
    document.body.style.paddingRight = previousBodyPaddingRight;
  };

  const resetFinishImagePan = () => {
    finishModalViewport.scrollLeft = 0;
  };

  const scheduleFinishImagePanReset = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resetFinishImagePan);
    });
  };

  const closeFinishImageModal = () => {
    finishImageModal.hidden = true;
    finishImageModal.setAttribute('aria-hidden', 'true');
    unlockFinishImageModalScroll();
    finishModalImage.removeAttribute('src');
    finishModalViewport.classList.remove(
      'finish-image-modal__viewport--close-target',
      'finish-image-modal__viewport--dragging',
    );

    if (lastFocusedFinish && document.body.contains(lastFocusedFinish)) {
      lastFocusedFinish.focus();
    }
  };

  const openFinishImageModal = finishItem => {
    lastFocusedFinish = finishItem;
    finishModalImage.setAttribute('src', finishItem.getAttribute('data-finish-modal-image'));
    finishModalImage.setAttribute('alt', '');
    finishModalTitle.textContent = finishItem.getAttribute('data-finish-modal-name');
    finishImageModal.hidden = false;
    finishImageModal.setAttribute('aria-hidden', 'false');
    lockFinishImageModalScroll();
    // Focus the dialog, not the Close button: focusing the button on open
    // makes some browsers (Safari especially) match :focus-visible on it and
    // flash its white "Close" box over the image. Keyboard users still reach
    // the button via the Tab handler below, where the box is the intended
    // affordance.
    finishModalDialog.focus();

    if (finishModalImage.complete) {
      scheduleFinishImagePanReset();
    } else {
      finishModalImage.addEventListener('load', scheduleFinishImagePanReset, { once: true });
    }
  };

  expandableFinishes.forEach(finishItem => {
    finishItem.addEventListener('click', () => openFinishImageModal(finishItem));
    finishItem.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openFinishImageModal(finishItem);
      }
    });
  });

  finishModalCloseButtons.forEach(button => {
    button.addEventListener('click', closeFinishImageModal);
  });

  finishImageModal.addEventListener('click', event => {
    if (finishImageModal.hidden) return;
    if (event.target.closest('[data-finish-modal-close]')) return;
    if (!isPointerOutsideFinishImage(event)) return;

    closeFinishImageModal();
  });

  finishModalViewport.addEventListener('pointerdown', event => {
    activePointerId = event.pointerId;
    finishModalViewport.setPointerCapture(activePointerId);
    panStartX = event.clientX;
    panStartY = event.clientY;
    hasDraggedFinishImage = false;
    panStartedOnImage = !isPointerOutsideFinishImage(event);
    finishModalViewport.classList.remove('finish-image-modal__viewport--close-target');
  });

  finishModalViewport.addEventListener('pointermove', event => {
    updateCloseTargetCursor(event);

    if (!panStartedOnImage) return;
    if (activePointerId !== event.pointerId) return;
    if (Math.hypot(event.clientX - panStartX, event.clientY - panStartY) < 4) return;

    hasDraggedFinishImage = true;
  });

  const stopFinishImagePan = event => {
    if (activePointerId !== event.pointerId) return;

    finishModalViewport.releasePointerCapture(activePointerId);
    activePointerId = null;
    panStartedOnImage = false;
    finishModalViewport.classList.remove('finish-image-modal__viewport--dragging');

    const clickedOutsideImage = isPointerOutsideFinishImage(event);

    if (!hasDraggedFinishImage && clickedOutsideImage) {
      closeFinishImageModal();
    } else {
      updateCloseTargetCursor(event);
    }
  };

  finishModalViewport.addEventListener('pointerup', stopFinishImagePan);
  finishModalViewport.addEventListener('pointercancel', stopFinishImagePan);
  finishModalViewport.addEventListener('pointerleave', () => {
    finishModalViewport.classList.remove('finish-image-modal__viewport--close-target');
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Tab' && !finishImageModal.hidden) {
      event.preventDefault();
      finishModalCloseButton.focus();
    }

    if (event.key === 'Escape' && !finishImageModal.hidden) {
      closeFinishImageModal();
    }
  });
};

if (document.readyState !== 'loading') {
  initializeFinishImageModal();
} else {
  document.addEventListener('DOMContentLoaded', initializeFinishImageModal);
}

export default initializeFinishImageModal;
